import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogContent, TextField } from "@mui/material";
import { postVisual } from "../api";
import { useToast } from "../../../components/Toast";

interface SingleImageToolProps {
  patientId: string;
  sessionId?: string;
  getToken: () => Promise<string | null>;
  promptLabelKey: string; // e.g. "experiential.containerDesc"
}

/* One symbolic image from one description (Safe Container). */
export function SingleImageTool({ patientId, sessionId, getToken, promptLabelKey }: SingleImageToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [presentOpen, setPresentOpen] = useState(false);

  const handleGenerate = async () => {
    try {
      setBusy(true);
      const res = await postVisual(getToken, {
        patient_id: patientId, session_id: sessionId, kind: "single", prompt,
      });
      setUrl(res.images[0].url);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg.includes("image_policy_declined") ? t("experiential.policyDeclined") : t("experiential.genError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <TextField
        label={t(promptLabelKey)}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Button variant="contained" size="small" onClick={handleGenerate} disabled={busy || !prompt.trim()} sx={{ mb: 2 }}>
        {t("experiential.generateImages")}
      </Button>

      {url && (
        <>
          <img src={url} alt={t(promptLabelKey)} style={{ width: "100%", borderRadius: 6 }} />
          <Button variant="outlined" size="small" onClick={() => setPresentOpen(true)} sx={{ mt: 2 }}>
            {t("experiential.present")}
          </Button>
          <Dialog open={presentOpen} onClose={() => setPresentOpen(false)} maxWidth="xl" fullWidth>
            <DialogContent sx={{ p: 0 }}>
              <img src={url} alt={t(promptLabelKey)} className="exp-present-img" />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
